import type { Lang } from '../data/cards'
/**
 * Тонкая обёртка над window.Telegram.WebApp.
 * Всё опционально: приложение полностью работает в обычном браузере
 * (для локальной разработки), просто без нативных кнопок и хаптики.
 */

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
type NotificationType = 'error' | 'success' | 'warning'

interface TgButton {
  text: string
  show(): void
  hide(): void
  enable(): void
  disable(): void
  setText(text: string): void
  onClick(cb: () => void): void
  offClick(cb: () => void): void
  showProgress(leaveActive?: boolean): void
  hideProgress(): void
  setParams(p: Record<string, unknown>): void
}

interface TgBackButton {
  /** Видна ли кнопка сейчас: нужно, чтобы вернуть её в прежнее состояние. */
  isVisible?: boolean
  show(): void
  hide(): void
  onClick(cb: () => void): void
  offClick(cb: () => void): void
}

export interface TelegramWebApp {
  initData: string
  initDataUnsafe: {
    user?: { id: number; first_name: string; last_name?: string; username?: string; language_code?: string; photo_url?: string }
    start_param?: string
  }
  version: string
  /** 'unknown', когда скрипт загружен в обычном браузере, а не в клиенте Telegram. */
  platform: string
  colorScheme: 'light' | 'dark'
  themeParams: Record<string, string>
  isExpanded: boolean
  viewportStableHeight: number
  MainButton: TgButton
  BackButton: TgBackButton
  HapticFeedback: {
    impactOccurred(style: HapticStyle): void
    notificationOccurred(type: NotificationType): void
    selectionChanged(): void
  }
  ready(): void
  expand(): void
  /** Bot API 8.0+: полноэкранный режим без шапки Telegram. */
  requestFullscreen?(): void
  exitFullscreen?(): void
  isFullscreen?: boolean
  /** Bot API 7.7+: запрет сворачивания приложения смахиванием вниз. */
  disableVerticalSwipes?(): void
  /** Bot API 8.0+: вырезы устройства (часы, чёлка) и место под кнопки Telegram. */
  safeAreaInset?: { top: number; bottom: number; left: number; right: number }
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number }
  close(): void
  setHeaderColor(color: string): void
  setBackgroundColor(color: string): void
  openInvoice(url: string, cb?: (status: string) => void): void
  /** Bot API 7.8+: публикация картинки в историю. */
  shareToStory(mediaUrl: string, params?: { text?: string; widget_link?: { url: string; name?: string } }): void
  /** Bot API 8.0+: системный выбор чата для заранее подготовленного сообщения. */
  shareMessage(preparedMessageId: string, cb?: (sent: boolean) => void): void
  downloadFile(params: { url: string; file_name: string }, cb?: (accepted: boolean) => void): void
  openTelegramLink(url: string): void
  onEvent(event: string, cb: () => void): void
  offEvent(event: string, cb: () => void): void
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

const webApp: TelegramWebApp | undefined = window.Telegram?.WebApp

/**
 * Скрипт telegram-web-app.js определяет window.Telegram.WebApp и в обычном
 * браузере — но там `platform === 'unknown'` и нативные кнопки невидимы.
 * Поэтому «мы внутри Telegram» решаем по платформе, а не по наличию объекта:
 * иначе в браузере не отрисуется ни нативная, ни запасная кнопка.
 */
export const inTelegram: boolean = Boolean(webApp && webApp.platform !== 'unknown')

/** WebApp только когда мы действительно внутри клиента Telegram. */
export const tg: TelegramWebApp | undefined = inTelegram ? webApp : undefined

/** Совпадает ли версия клиента с требуемой (некоторые API есть только с 6.1+). */
export function supports(minVersion: string): boolean {
  if (!tg) return false
  const a = tg.version.split('.').map(Number)
  const b = minVersion.split('.').map(Number)
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0
    const y = b[i] ?? 0
    if (x !== y) return x > y
  }
  return true
}

/**
 * Переносит отступы Telegram в CSS.
 *
 * В полноэкранном режиме приложение занимает весь экран, включая полосу с
 * часами и область, где висят плавающие кнопки Telegram. Без этих отступов
 * наш заголовок оказался бы под ними.
 *
 * `safeAreaInset` — вырезы самого устройства, `contentSafeAreaInset` — место,
 * занятое интерфейсом Telegram. Складываем: обходить нужно и то, и другое.
 */
function applyInsets(): void {
  if (!tg) return
  const device = tg.safeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 }
  const content = tg.contentSafeAreaInset ?? { top: 0, bottom: 0, left: 0, right: 0 }
  const root = document.documentElement.style
  root.setProperty('--tg-inset-top', `${device.top + content.top}px`)
  root.setProperty('--tg-inset-bottom', `${device.bottom + content.bottom}px`)
  root.setProperty('--tg-inset-left', `${device.left + content.left}px`)
  root.setProperty('--tg-inset-right', `${device.right + content.right}px`)
}

export function initTelegram(): void {
  if (!tg) return
  tg.ready()
  tg.expand()

  // Полный экран: шапка Telegram с именем бота исчезает, приложение занимает
  // всё до часов. Своя кнопка «назад» в заголовке уже есть, так что потери
  // системной шапки человек не заметит.
  if (supports('8.0')) {
    try {
      tg.requestFullscreen?.()
    } catch {
      /* клиент отказал — остаёмся в обычном режиме, приложение работает */
    }
  }

  // Смахивание вниз сворачивает приложение прямо посреди расклада —
  // на экране с картами это чистая помеха.
  if (supports('7.7')) {
    try {
      tg.disableVerticalSwipes?.()
    } catch {
      /* не критично */
    }
  }

  applyInsets()
  // Отступы меняются при повороте экрана и при входе в полноэкранный режим.
  for (const event of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) {
    try {
      tg.onEvent(event, applyInsets)
    } catch {
      /* событие неизвестно этому клиенту */
    }
  }
  // Тема приложения фиксированная («богатая эзотерика»), поэтому подгоняем
  // хром Telegram под наш фон, а не наоборот.
  try {
    tg.setHeaderColor('#0d0819')
    tg.setBackgroundColor('#0d0819')
  } catch {
    /* старые клиенты — игнорируем */
  }
}

export const haptic = {
  impact(style: HapticStyle = 'light') {
    if (supports('6.1')) tg?.HapticFeedback.impactOccurred(style)
  },
  notify(type: NotificationType) {
    if (supports('6.1')) tg?.HapticFeedback.notificationOccurred(type)
  },
  select() {
    if (supports('6.1')) tg?.HapticFeedback.selectionChanged()
  },
}

/**
 * Язык из настроек Telegram или браузера.
 *
 * Русский держим первым предположением для стран, где приложение уже живёт:
 * телефон на русском там обычнее всего. Незнакомый язык уводим в английский,
 * а не в русский — для остального мира это ближе.
 */
export function detectLang(): Lang {
  const code = (tg?.initDataUnsafe.user?.language_code ?? navigator.language ?? '').toLowerCase()
  if (code.startsWith('az')) return 'az'
  if (code.startsWith('ru')) return 'ru'
  if (code.startsWith('tr')) return 'tr'
  return 'en'
}

export function telegramUser() {
  return tg?.initDataUnsafe.user
}

/** initData для подписи запросов к бэкенду. Пустая строка вне Telegram. */
export function initData(): string {
  return tg?.initData ?? ''
}
